import { useEffect, useMemo, useState } from "hono/jsx";
import { ElectionData, Office } from "../mocks.ts";
import { LeafletMapIsland } from "./LeafletMapIsland.tsx";

interface ElectionMapProps {
  data: ElectionData;
  scriptUrl: string;
}

type ViewMode = "current" | "primary" | "general";

export function ElectionMap({ data, scriptUrl }: ElectionMapProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("current");
  const [selectedOfficeId, setSelectedOfficeId] = useState<string>(
    data.offices[0].id,
  );
  const [selectedBillId, setSelectedBillId] = useState<string | null>(null);
  const [selectedDistrictId, setSelectedDistrictId] = useState<string | null>(
    null,
  );

  // Reset specifics when switching high-level modes
  const handleModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    setSelectedBillId(null);
    setSelectedDistrictId(null);
  };

  const selectedOffice = useMemo(
    () =>
      data.offices.find((o) => o.id === selectedOfficeId) || data.offices[0],
    [selectedOfficeId, data.offices],
  );

  const selectedBill = useMemo(
    () => data.bills.find((b) => b.id === selectedBillId),
    [selectedBillId, data.bills],
  );

  // Determine which districts to show based on selected office
  const currentLayerId = selectedOffice.districtLayerId;
  const currentDistricts = data.districtLayers[currentLayerId] || [];

  // Calculate map styling based on selection
  const mapData = useMemo(() => {
    const styles: Record<string, { color: string; label?: string }> = {};

    currentDistricts.forEach((district) => {
      let color = "#ccc"; // Default
      let label = district.name;

      const geoId = district.geoId;
      if (!geoId) return;

      if (viewMode === "current") {
        // Find representative for this district AND office (IMPORTANT!)
        const rep = data.representatives.find(
          (r) =>
            r.districtId === district.id && r.officeId === selectedOffice.id,
        );

        if (selectedBill && rep && selectedOffice.type === "us_house") { // Only bills for US House right now
          const vote = selectedBill.votes[rep.id];
          if (vote === "yea") color = "#22c55e"; // Green
          else if (vote === "nay") color = "#ef4444"; // Red
          else color = "#eab308"; // Yellow/Abstain

          label = `${district.name}: ${rep.name} (${
            vote?.toUpperCase() || "NV"
          })`;
        } else {
          color = district.color || "#ccc";
          if (rep) {
            label = `${district.name}: ${rep.name} (${rep.party})`;
          }
        }
      } else if (viewMode === "primary") {
        const race = data.races.find(
          (r) =>
            r.districtId === district.id &&
            r.officeId === selectedOffice.id &&
            r.type.includes("primary"),
        );

        if (race) {
          const count = race.candidates.length;
          color = count > 1 ? "#ef4444" : "#fca5a5";
          if (race.type === "primary_dem") color = "#3b82f6";

          const candidatesNames = race.candidates
            .map((cid) => data.candidates.find((c) => c.id === cid)?.name)
            .join(", ");
          label = `${district.name} Primary: ${candidatesNames}`;
        } else {
          color = "#f3f4f6";
        }
      } else if (viewMode === "general") {
        const race = data.races.find(
          (r) =>
            r.districtId === district.id &&
            r.officeId === selectedOffice.id &&
            r.type === "general",
        );
        if (race) {
          color = "#a855f7";
          label = `${district.name} General Election`;
        } else {
          color = "#f3f4f6";
        }
      }

      styles[geoId] = { color, label };
    });

    return styles;
  }, [selectedBill, data, viewMode, selectedOffice, currentDistricts]);

  return (
    <div class="flex flex-col gap-4 p-4 items-center w-full max-w-6xl mx-auto">
      <h1 class="text-3xl font-bold font-header text-base-content uppercase">
        {data.region} Representation & Elections
      </h1>

      <div class="tabs tabs-boxed w-full max-w-md mx-auto mb-4">
        <a
          class={`tab ${viewMode === "current" ? "tab-active" : ""}`}
          onClick={() => handleModeChange("current")}
        >
          Current Reps
        </a>
        <a
          class={`tab ${viewMode === "primary" ? "tab-active" : ""}`}
          onClick={() => handleModeChange("primary")}
        >
          Primaries
        </a>
        <a
          class={`tab ${viewMode === "general" ? "tab-active" : ""}`}
          onClick={() => handleModeChange("general")}
        >
          General
        </a>
      </div>

      <div class="w-full grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Controls */}
        <div class="lg:col-span-1 flex flex-col gap-4">
          {/* Office Selector - NEW */}
          <div class="card bg-base-100 shadow-xl border border-base-300">
            <div class="card-body p-4">
              <h2 class="card-title text-sm uppercase opacity-50 font-black">
                Filters
              </h2>
              <label class="form-control w-full">
                <div class="label">
                  <span class="label-text text-xs font-bold">
                    OFFICE / LEVEL
                  </span>
                </div>
                <select
                  class="select select-bordered select-sm w-full"
                  value={selectedOfficeId}
                  onChange={(e) => {
                    setSelectedOfficeId((e.target as HTMLSelectElement).value);
                    setSelectedDistrictId(null); // Reset selection
                  }}
                >
                  {data.offices.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {viewMode === "current" && selectedOffice.type === "us_house" && (
            <div class="card bg-base-100 shadow-xl border border-base-300">
              <div class="card-body p-4">
                <h2 class="card-title text-sm uppercase opacity-50 font-black">
                  Voting Record
                </h2>
                <select
                  class="select select-bordered select-sm w-full"
                  value={selectedBillId || ""}
                  onChange={(e) =>
                    setSelectedBillId(
                      (e.target as HTMLSelectElement).value || null,
                    )}
                >
                  <option value="">Select Bill...</option>
                  <optgroup label="2024 Session">
                    {data.bills.map((bill) => (
                      <option key={bill.id} value={bill.id}>
                        {bill.title}
                      </option>
                    ))}
                  </optgroup>
                </select>
                {selectedBill && (
                  <div class="mt-4 animate-in fade-in">
                    <h3 class="font-bold text-sm leading-tight mb-1">
                      {selectedBill.title}
                    </h3>
                    <p class="text-xs opacity-70 mb-2">
                      {selectedBill.description}
                    </p>
                    <div class="flex flex-col gap-1 text-xs font-bold uppercase">
                      <div class="flex items-center gap-2">
                        <div class="w-2 h-2 rounded-full bg-green-500"></div>
                        {" "}
                        YEAH
                      </div>
                      <div class="flex items-center gap-2">
                        <div class="w-2 h-2 rounded-full bg-red-500"></div> NAY
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {viewMode !== "current" && (
            <div class="card bg-base-100 shadow-xl border border-base-300">
              <div class="card-body p-4">
                <h2 class="card-title text-sm uppercase opacity-50 font-black">
                  {viewMode === "primary"
                    ? "Active Primaries"
                    : "General Election"}
                </h2>

                {selectedDistrictId
                  ? (() => {
                    const district = currentDistricts.find((d) =>
                      d.geoId === selectedDistrictId ||
                      d.id === selectedDistrictId
                    );
                    const race = data.races.find((r) =>
                      r.districtId === district?.id &&
                      r.officeId === selectedOffice.id &&
                      (viewMode === "general"
                        ? r.type === "general"
                        : r.type.includes("primary"))
                    );

                    if (!district) {
                      return <div class="text-error">District not found</div>;
                    }

                    return (
                      <div class="animate-in fade-in">
                        <h3 class="font-bold text-lg">{district.name}</h3>
                        {race
                          ? (
                            <div class="mt-2 flex flex-col gap-2">
                              <div class="badge badge-primary badge-outline">
                                {race.type.replace("_", " ").toUpperCase()}
                              </div>
                              <div class="text-xs opacity-70">
                                Election Date: {race.date}
                              </div>

                              <div class="divider my-1">Candidates</div>
                              {race.candidates.map((cid) => {
                                const c = data.candidates.find((cand) =>
                                  cand.id === cid
                                );
                                if (!c) return null;
                                return (
                                  <div
                                    key={cid}
                                    class="flex items-center gap-2 bg-base-200 p-2 rounded"
                                  >
                                    <div
                                      class={`w-2 h-2 rounded-full ${
                                        c.party === "Republican"
                                          ? "bg-red-500"
                                          : "bg-blue-500"
                                      }`}
                                    >
                                    </div>
                                    <div class="flex-1 font-bold text-sm">
                                      {c.name}
                                    </div>
                                    {c.isIncumbent && (
                                      <div class="badge badge-xs badge-ghost">
                                        Incumbent
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )
                          : (
                            <div class="text-sm opacity-50 mt-2 italic">
                              No active race tracked for this view.
                            </div>
                          )}
                      </div>
                    );
                  })()
                  : (
                    <div>
                      <p class="text-xs opacity-70">
                        Select a district on the map.
                      </p>
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>

        {/* Map Area */}
        <div class="lg:col-span-3 bg-base-100 rounded-xl shadow-lg border border-base-300 overflow-hidden min-h-[600px] relative">
          <LeafletMapIsland
            options={{
              initialData: mapData,
              layerId: currentLayerId, // Dynamic layer ID
              onDistrictClick: (id) => setSelectedDistrictId(id),
            }}
          />
          {/* Script Loader */}
          <script type="module" src={scriptUrl}></script>
          <link
            rel="stylesheet"
            href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
          />
        </div>
      </div>
    </div>
  );
}
